import { useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2, Sparkles, CalendarClock } from "lucide-react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function pickMime() {
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const t of types) if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t;
  return "";
}

export function VoiceNoteButton({ entityType, entityId, company, onSaved }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [form, setForm] = useState({ transcript: "", summary: "", follow_up_date: "", next_action: "" });
  const [saving, setSaving] = useState(false);

  const mr = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const timer = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const start = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Voice recording isn't supported on this browser.");
      return;
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Microphone permission denied.");
      return;
    }
    const mime = pickMime();
    chunks.current = [];
    mr.current = new MediaRecorder(stream.current, mime ? { mimeType: mime } : undefined);
    mr.current.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
    mr.current.onstop = () => upload(mime);
    mr.current.start();
    setRecording(true);
    setSeconds(0);
    timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  };

  const stop = () => {
    clearInterval(timer.current);
    setRecording(false);
    if (mr.current && mr.current.state !== "inactive") mr.current.stop();
    if (stream.current) stream.current.getTracks().forEach((t) => t.stop());
  };

  const upload = async (mime) => {
    setProcessing(true);
    try {
      const ext = (mime || "audio/webm").includes("mp4") ? "mp4" : (mime || "").includes("ogg") ? "ogg" : "webm";
      const blob = new Blob(chunks.current, { type: mime || "audio/webm" });
      if (blob.size < 800) { toast.error("Recording too short. Try again."); return; }
      const fd = new FormData();
      fd.append("audio", blob, `note.${ext}`);
      const { data } = await api.post("/voice/transcribe", fd);
      if (!data.transcript) { toast.error("Couldn't hear anything. Try again."); return; }
      setForm({
        transcript: data.transcript,
        summary: data.summary || data.transcript,
        follow_up_date: data.follow_up_date || "",
        next_action: data.next_action || "",
      });
      setReviewOpen(true);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setProcessing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/voice/save-note", {
        entity_type: entityType, entity_id: entityId,
        transcript: form.transcript, summary: form.summary,
        follow_up_date: form.follow_up_date || null, next_action: form.next_action,
      });
      toast.success(form.follow_up_date ? `Note saved · reminder set for ${form.follow_up_date}` : "Voice note saved");
      setReviewOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {recording ? (
        <Button onClick={stop} className="gap-1.5 bg-red-600 hover:bg-red-700 text-white animate-pulse" data-testid="voice-stop-btn">
          <Square className="h-4 w-4" /> Stop · {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </Button>
      ) : (
        <Button variant="outline" onClick={start} disabled={processing} className="gap-1.5" data-testid="voice-note-btn">
          {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4 text-brand" />}
          {processing ? "Transcribing…" : "Voice Note"}
        </Button>
      )}

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-lg" data-testid="voice-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand" /> Review Voice Note</DialogTitle>
            <DialogDescription>{company ? `For ${company}. ` : ""}Edit before saving. A follow-up reminder will fire on the date below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">CRM Note (AI summary)</Label>
              <Textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={3} data-testid="voice-summary" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Follow-up Date</Label>
                <Input type="date" value={form.follow_up_date || ""} onChange={(e) => set("follow_up_date", e.target.value)} className="h-9" data-testid="voice-followup-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Next Action</Label>
                <Input value={form.next_action} onChange={(e) => set("next_action", e.target.value)} className="h-9" placeholder="e.g. Send revised quote" data-testid="voice-next-action" />
              </div>
            </div>
            <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Transcript</p>
              <p className="text-xs text-slate-500 italic">“{form.transcript}”</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)} data-testid="voice-cancel">Discard</Button>
            <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand/90 text-white gap-2" data-testid="voice-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
